import React, { useState } from 'react'
import { Box, Stack, Tab, Tabs } from '@mui/material'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface BeforeYouBookTab {
    id: string
    label: string
    body: React.ReactNode
}

interface BeforeYouBookProps {
    tabs: BeforeYouBookTab[]
    isMobile: boolean
}

/**
 * The small print, in one place with a tab per question: what you need to enter the country, what
 * you pay and when, and the contract itself.
 *
 * Stacked as four separate sections these ran to several screens of prose that nobody reaches,
 * and the cancellation terms — the part most often looked up — sat furthest down.
 */
const BeforeYouBook: React.FC<BeforeYouBookProps> = ({ tabs, isMobile }) => {
    const [activeId, setActiveId] = useState(tabs[0]?.id)

    const active = tabs.find(tab => tab.id === activeId) ?? tabs[0]
    if (!active) return null

    return (
        <Box
            sx={{
                borderRadius: HOLIDAYS_RADIUS.NESTED,
                border: '1px solid',
                borderColor: 'borders.default',
                overflow: 'hidden'
            }}
        >
            <Tabs
                value={active.id}
                onChange={(_event, value: string) => setActiveId(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    minHeight: 'unset',
                    bgcolor: 'neutral.100',
                    borderBottom: '1px solid',
                    borderColor: 'borders.default',
                    '& .MuiTabs-indicator': { height: 3, bgcolor: 'primary.main' }
                }}
            >
                {tabs.map(tab => (
                    <Tab
                        key={tab.id}
                        value={tab.id}
                        label={tab.label}
                        sx={{
                            minHeight: 'unset',
                            px: isMobile ? 2 : 3,
                            py: 1.75,
                            fontSize: isMobile ? '0.875rem' : '0.938rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            color: 'textColors.subtitle',
                            '&.Mui-selected': { color: 'textColors.primary', fontWeight: 700 }
                        }}
                    />
                ))}
            </Tabs>

            {/*
             * Every panel stays mounted and the inactive ones are hidden with CSS. These pages are
             * indexed, and cancellation terms that exist only after a click are terms a crawler —
             * and a reader pressing Ctrl+F — never sees.
             */}
            {tabs.map(tab => (
                <Stack
                    key={tab.id}
                    role="tabpanel"
                    hidden={tab.id !== active.id}
                    spacing={isMobile ? 2.5 : 3}
                    sx={{ p: isMobile ? 2 : 3, ...(tab.id !== active.id && { display: 'none' }) }}
                >
                    {tab.body}
                </Stack>
            ))}
        </Box>
    )
}

export { BeforeYouBook }
export default BeforeYouBook
